<template>
    <LeftAsideSubLayout
        :title="`Resources: Manufacturer Literature / ${manufacturer.name}`"
        :useBackButton="true"
        :backButtonUrl="route('web.catalog.resources.index')"
    >
        <template #aside>
            <div class="p-6">
                <div class="mb-6">
                    <SeclockMultiselect
                        :option-is-object="true"
                        label-by="display_name"
                        track-by="display_name"
                        value-by="display_name"
                        v-model="selectedManufacturer"
                        :options="manufacturers"
                        :searchable="true"
                        :placeholder="$t('Manufacturers')"
                        :can-clear="false"
                        :large="false"
                        mode="single"
                        @select="navigateToShow($event)"
                    />
                </div>
                <div
                    class="w-full mb-8 p-6 rounded text-sm bg-gray-150 type-tertiary"
                >
                    <img
                        :class="{
                            'w-80 h-auto at1024:mx-auto at1024:px-0 mr-auto' : !settings.is_sourcing_mode,
                        }"
                        :src="useAssetTransforms().marketingAssetUrl(manufacturer.logo)"
                        :alt="manufacturer.logo.name ?? manufacturer.name"
                    />
                </div>
                <div
                    v-if="manufacturer.channels?.find(channel => channel?.pivot.type === 1)?.channel"
                    class="flex items-center gap-2"
                >
                    <BaseSvg name="icon-local-phone" class="w-5 h-5 shrink-0 fill-tertiary" aria-hidden="true" />
                    <a :href="'tel:' + manufacturer.channels?.find(channel => channel?.pivot.type === 1)?.channel">{{ manufacturer.channels.find(channel => channel?.pivot.type === 1)?.channel }}</a>
                </div>
                <div
                    v-if="manufacturer.channels?.find(channel => channel?.pivot.type === 4)?.channel"
                    class="flex items-center gap-2 text-sm"
                >
                    <BaseSvg name="icon-earth-globe" class="w-5 h-5 shrink-0 fill-tertiary" aria-hidden="true" />
                    <a
                        target="_blank"
                        :href="manufacturer.channels?.find(channel => channel?.pivot.type === 4)?.channel"
                        class="hover:decoration-solid break-all underline underline-offset-4 decoration-dotted overflow-hidden whitespace-nowrap truncate"
                    >
                        <span aria-hidden="true">{{ manufacturer.channels?.find(channel => channel?.pivot.type === 4)?.channel }}</span>
                        <span class="sr-only">Visit the {{ manufacturer?.name }} website</span>
                    </a>
                </div>
                <div class="border-b border-keyline-primary my-6"></div>
                <div>
                    <div class="mb-2 type-primary">{{ $t("Product categories") }}</div>
                    <ul class="flex flex-col w-full mb-2">
                        <li v-for="tag in manufacturer.tags['item-category'] ?? []">
                            <a
                                :href="
                                    route('web.catalog.products.index', {
                                        filters: {
                                            0: {
                                                field: 'filter_category',
                                                values: [tag.name],
                                                type: 'any',
                                            },
                                            1: {
                                                field: 'filter_manufacturer',
                                                values: [manufacturer.name],
                                                type: 'any',
                                            },
                                        },
                                    })
                                "
                                class="whitespace-nowrap truncate active:bg-gray-tier-6 text-left w-full rounded-sm font-medium block py-2.5 px-3 hover:type-primary hover:bg-gray-tier-5 type-secondary bg-inherit"
                            >
                                {{ tag.name }}
                            </a>
                        </li>
                    </ul>
                    <a
                        :href="
                            route('web.catalog.products.index', {
                                filters: {
                                    1: {
                                        field: 'filter_manufacturer',
                                        values: [manufacturer.name],
                                        type: 'any',
                                    },
                                },
                            })
                        "
                        class="text-sm w-fit flex items-center gap-1 type-secondary underline underline-offset-4 decoration-dotted hover:decoration-solid hover:type-primary"
                    >
                        {{ $t("View all products") }} <BaseSvg name="icon-arrow-right-alt" class="w-5 h-5 shrink-0" aria-hidden="true" />
                    </a>
                </div>
            </div>
        </template>
        <template #default>
            <div class="p-10 pt-8">
                <h2 class="type-primary font-bold text-2xl mb-4">{{ manufacturer.name }}</h2>
                <div class="flex items-center mb-4">
                    <SeclockMultiselect
                        v-model="selectedSeries"
                        :options="computed_series ?? []"
                        placeholder="All Series"
                        :close-on-select="true"
                        :searchable="true"
                        :hide-selected="true"
                        label-by="name"
                        track-by="name"
                        value-by="name"
                        :option-is-object="true"
                        :large="true"
                        :disabled="!computed_series.length"
                        class="w-full"
                    />
                </div>
                <ResourceList v-if="filteredResources" :resources="filteredResources"/>
                <RecordsNotFound v-else icon="icon-draft-paper" header="Resources unavailable"/>
            </div>
        </template>
    </LeftAsideSubLayout>
</template>

<script setup>
import {
    BaseSvg,
    SeclockMultiselect,
    ResourceList,
    RecordsNotFound,
} from "@/Components";
import { computed, ref } from "vue";
import { useAssetTransforms, useUser } from "@/Composables";
import {router} from "@inertiajs/vue3";
import LeftAsideSubLayout from "@/Layouts/Modules/SubLayouts/LeftAsideSubLayout.vue";

const { settings } = useUser();

const { marketingAssetUrl } = useAssetTransforms();
const props = defineProps(["resources", "manufacturer", "crumbs", 'manufacturers']);
const selectedSeries = ref(null);
const selectedManufacturer = ref(props.manufacturers.find(company => company.display_name === props.manufacturer.name));

const navigateToShow = (event) => router.visit(route('web.catalog.resources.show', event.slug));

const clearSelect = () => (selectedSeries.value = null);

const filteredResources = computed(() => {
    let resources;
    if (selectedSeries.value?.id) {
        resources = Object.entries(props.manufacturer.resources)
            .map(([key, value]) => [
                key,
                value.filter((resource) =>
                    resource.series.includes(selectedSeries.value.id)
                )
            ])
            .filter(([_, filteredResources]) => filteredResources.length > 0)
            .reduce((obj, [key, filteredResources]) => {
                obj[key] = filteredResources;
                return obj;
            }, {});
    } else {
        resources = props.manufacturer?.resources;
    }

    // Sort the resources by key in alphabetical order and sort resources within each group
    return Object.keys(resources)
        .sort()
        .reduce((obj, key) => {
            // Sort resources within each group alphabetically by name
            obj[key] = [...(resources?.[key] || [])].sort((a, b) => {
                const nameA = a.name?.toLowerCase() ?? '';
                const nameB = b.name?.toLowerCase() ?? '';
                return nameA.localeCompare(nameB);
            });
            return obj;
        }, {});
});


const computed_series = computed(() => {
    //Add the empty entry "All Series" to the drop down selections
    return [{name: 'All Series'}, ...props.manufacturer?.series]
});
</script>
